import { unstable_isUnrecognizedActionError } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';

type Capture = {
  eventId?: string;
  outcome?: string | null;
  listeners: Set<(id: string | null) => void>;
};

const captures = new WeakMap<Error, Capture>();
// Only metadata, not exceptions. Bound entries for transports that never answer.
const pending = new Map<string, Capture>();
const installed = new WeakSet<object>();

function track(error: Error): Capture {
  let capture = captures.get(error);
  if (!capture) {
    capture = { listeners: new Set() };
    captures.set(error, capture);
  }
  return capture;
}

/** Install before hydration so a global handler's earlier capture keeps its ID. */
export function installRouteErrorTracking(): void {
  const client = Sentry.getClient();
  if (!client || installed.has(client)) {
    return;
  }
  installed.add(client);
  client.on('preprocessEvent', (event, hint) => {
    if (
      !event.type &&
      hint?.originalException instanceof Error &&
      event.event_id
    ) {
      track(hint.originalException).eventId = event.event_id;
    }
  });
  client.on('beforeSendEvent', (event, hint) => {
    if (
      !event.type &&
      hint?.originalException instanceof Error &&
      event.event_id
    ) {
      pending.set(event.event_id, track(hint.originalException));
      if (pending.size > 1000) {
        const oldest = pending.keys().next().value;
        if (oldest) {
          pending.delete(oldest);
        }
      }
    }
  });
  client.on('afterSendEvent', (event, response) => {
    const capture = event.event_id && pending.get(event.event_id);
    if (capture) {
      pending.delete(event.event_id!);
      const status = response?.statusCode;
      capture.outcome =
        status !== undefined && status >= 200 && status < 300
          ? event.event_id!
          : null;
      for (const finish of capture.listeners) {
        finish(capture.outcome);
      }
    }
  });
}

/** Reuse the original event and grouping, linking only a transport-accepted ID. */
export function captureRouteError(
  error: Error & { digest?: string },
  boundary: string
): Promise<string | null> {
  if (!Sentry.getClient()) {
    return Promise.resolve(null);
  }
  installRouteErrorTracking();
  const previous = captures.get(error);
  const capture = previous ?? track(error);
  const result =
    capture.outcome !== undefined
      ? Promise.resolve(capture.outcome)
      : new Promise<string | null>((resolve) => {
          const finish = (id: string | null) => {
            clearTimeout(timeout);
            capture.listeners.delete(finish);
            resolve(id);
          };
          const timeout = setTimeout(() => finish(null), 5000);
          capture.listeners.add(finish);
        });
  if (!previous) {
    // Ignore captureException's return value: if a prior capture is still being
    // prepared, SDK object deduplication sends nothing here. Its hooks supply the real ID.
    const staleAction: boolean = unstable_isUnrecognizedActionError(error);
    Sentry.captureException(
      error,
      staleAction
        ? {
            level: 'warning',
            fingerprint: ['browser', 'deployment-skew', 'unrecognized-action'],
            tags: { feature: 'deployment-skew', digest: error.digest },
          }
        : {
            tags: {
              digest: error.digest,
              feature: 'route-error-boundary',
              boundary,
            },
          }
    );
  }
  return result;
}
